import React, { FunctionComponent, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import styled from 'styled-components'
import { Box, Flex, Text as ThemeUIText } from 'theme-ui'

import { Chevron, baseColors, makeTransition } from '@fairhq/common'

import { useCharlieUser } from 'hooks/useCharlieUser'
import { useDropdown } from 'hooks/useDropdown'
import { useGetStrategyQuery } from 'store/strategy/strategyApi'
import {
  useEditTaskMutation,
  useGetTaskStatusOptionsQuery,
} from 'store/tasks/tasksApi'
import { Task, TaskStatus } from 'store/tasks/types'
import { insightsTaskStatusChangedEvent } from 'utils/analytics/amplitudeEvents'
import { getTaskStatusColors } from 'utils/getTaskStatusColor'

const StyledChevron = styled(Chevron)`
  margin-left: 16px;
`

interface StatusDropdownProps {
  status: TaskStatus
  task: Task
  setSkipTask: () => void
  setCompleteTask: () => void
  setCurrentstatus: (s: TaskStatus) => void
  parentId?: number
}

export const StatusDropdown: FunctionComponent<StatusDropdownProps> = ({
  status,
  task,
  setSkipTask,
  setCompleteTask,
  setCurrentstatus,
  parentId = 0,
}) => {
  const { t } = useTranslation()

  const { data: statusOptions, isSuccess } = useGetTaskStatusOptionsQuery()
  const { data: strategy, refetch: refetchStrategy } = useGetStrategyQuery()
  const [editTask, { isSuccess: isEditTaskSuccess }] = useEditTaskMutation()

  const { id: taskId } = task
  const charlieUser = useCharlieUser()

  const {
    ref,
    opened: isDropdownOpen,
    toggleDropdown,
    closeDropdown,
    openDropdown,
  } = useDropdown()

  useEffect(() => {
    if (isDropdownOpen) {
      const element = document.getElementById(`options_list_0`)

      if (element) {
        element.focus({ preventScroll: true })
      }
    }
  }, [isDropdownOpen])

  useEffect(() => {
    if (isEditTaskSuccess) {
      if (status === TaskStatus.SKIPPED) {
        setSkipTask()
      } else if (status === TaskStatus.COMPLETED && task.linkedQuestionCode) {
        setCompleteTask()
      } else {
        refetchStrategy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditTaskSuccess, status, refetchStrategy])

  const handleClick = async ({
    option,
    taskId,
  }: {
    option: TaskStatus
    taskId: number
  }) => {
    setCurrentstatus(option)
    await editTask({
      id: taskId,
      status: option,
      sessionId: strategy?.sessionId,
    })
    insightsTaskStatusChangedEvent({ taskId, option }, charlieUser)
    handleCloseDropdown()
  }

  const handleKeyDownSelect = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      openDropdown()
    }

    if (isDropdownOpen && e.key === 'Escape') {
      handleCloseDropdown()
    }
  }
  const handleKeyDownOption = (
    e: React.KeyboardEvent<HTMLDivElement>,
    option: TaskStatus,
    index: number
  ) => {
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault()
      const sibling = document.getElementById(`options_list_${index + 1}`)
      if (sibling) {
        sibling.focus()
      }
    }

    if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault()
      const sibling = document.getElementById(`options_list_${index - 1}`)
      if (sibling) {
        sibling.focus()
      }
    }

    if (e.key === 'Enter') {
      handleClick({ option, taskId })
    }
  }

  const handleCloseDropdown = () => {
    closeDropdown()
    const element = document.getElementById(
      `status_dropdown_${parentId}_${taskId}`
    )
    if (element) {
      element.focus()
    }
  }

  return (
    <>
      {isSuccess && statusOptions.length > 0 && (
        <Flex
          tabIndex={0}
          ref={ref}
          id={`status_dropdown_${parentId}_${taskId}`}
          sx={{
            position: 'relative',
            height: 48,
            px: '16px',
            alignItems: 'center',
            borderRadius: '4px',
            userSelect: 'none',
            cursor: 'pointer',
            svg: { mr: 1 },
            border: '1px solid',
            ...getTaskStatusColors(status),
            borderColor: isDropdownOpen
              ? baseColors.statesFocus
              : getTaskStatusColors(status).borderColor,
            flexShrink: 0,
            ':focus-visible': {
              outline: `2px solid ${baseColors.statesFocus}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
          }}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDownSelect}
          role="button"
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
        >
          <ThemeUIText as="p" sx={{ fontWeight: 400, width: '90px' }}>
            {t(`tasks.status.${status}`)}
          </ThemeUIText>
          <StyledChevron />
          {isDropdownOpen && (
            <Box
              tabIndex={0}
              as="ul"
              id={`options_list_${parentId}_${taskId}`}
              sx={{
                position: 'absolute',
                zIndex: 1000,
                top: '100%',
                left: 0,
                width: '100%',
                mt: 1,
                bg: 'white',
                borderRadius: 4,
                overflow: 'hidden',
                p: 0,
                border: '1px solid',
                borderColor: baseColors.statesFocus,
              }}
              role="listbox"
              aria-multiselectable="false"
              aria-label="task status options list"
            >
              {statusOptions?.map((option, index) => (
                <Flex
                  as="li"
                  tabIndex={0}
                  id={`options_list_${index}`}
                  className={option}
                  key={option}
                  sx={{
                    px: 2,
                    py: 1,
                    bg:
                      option === status
                        ? baseColors.backgroundsPrimarySurface
                        : '',
                    transition: makeTransition('background'),
                    '&:hover': {
                      bg: baseColors.statesHoverBackground,
                      color: baseColors.contentInverseContent,
                    },
                    color: baseColors.grey500,
                    border: `2px solid transparent`,
                    ':focus-visible': {
                      border: `2px solid ${baseColors.statesFocus}`,
                      borderRadius: '4px',
                      outline: 'none',
                    },
                  }}
                  onKeyDown={e => {
                    handleKeyDownOption(e, option, index)
                  }}
                  onClick={() => handleClick({ option, taskId })}
                  role="option"
                  aria-selected={status === option}
                >
                  <ThemeUIText as="div">
                    {t(`tasks.status.${option}`)}
                  </ThemeUIText>
                </Flex>
              ))}
            </Box>
          )}
        </Flex>
      )}
    </>
  )
}
